/**
 * F38 (Wave 2 Track D) — admin "grant / extend trial" modal.
 *
 * Composes Modal + FormField + NumberInput (optional trial days) + Textarea
 * (mandatory reason ≥10). Case-by-case admin trial grant/extend (resets
 * `trial_consumed` per admin judgment — D6). Gated server-side on
 * `venue_subscription:override`.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, NumberInput, Textarea } from '@bravemove/ui-web';
import { SUBSCRIPTION_TRIAL_DURATION_DAYS, type GrantTrialInput } from '@bravemove/types';

export interface GrantTrialDialogProps {
  readonly open: boolean;
  readonly venueLabel: string;
  readonly onConfirm: (input: GrantTrialInput) => Promise<void>;
  readonly onClose: () => void;
}

export function GrantTrialDialog({ open, venueLabel, onConfirm, onClose }: GrantTrialDialogProps) {
  const { t } = useI18n();
  const [trialDays, setTrialDays] = useState<number>(SUBSCRIPTION_TRIAL_DURATION_DAYS);
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    /* eslint-disable react-hooks/set-state-in-effect */
    setTrialDays(SUBSCRIPTION_TRIAL_DURATION_DAYS);
    setReason('');
    setServerError(null);
    setSubmitting(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open]);

  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= 10 && reasonTrimmed.length <= 500;
  const daysValid = Number.isInteger(trialDays) && trialDays >= 1 && trialDays <= 365;
  const disableSubmit = submitting || !reasonValid || !daysValid;

  const submit = async () => {
    setServerError(null);
    if (!reasonValid || !daysValid) {
      setServerError(
        t('admin:subscription.dialog.grant_trial.error_invalid', {
          defaultValue: 'Provide 1–365 trial days and a reason (10–500 characters).',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({ reason: reasonTrimmed, trialDays });
      onClose();
    } catch {
      setServerError(
        t('admin:subscription.toast.error_generic', {
          defaultValue: 'Something went wrong. Please try again.',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:subscription.dialog.grant_trial.title', { defaultValue: 'Grant trial' })}
      description={t('admin:subscription.dialog.grant_trial.description', {
        defaultValue: 'Start (or extend) a Pro trial for this venue. Audit-logged.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="grant-trial-cancel"
          >
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="grant-trial-confirm"
          >
            {t('admin:subscription.dialog.grant_trial.submit', { defaultValue: 'Grant trial' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="grant-trial-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="text-[12px] text-ink-3">
          {t('admin:subscription.detail.venue', { defaultValue: 'Venue' })}:{' '}
          <span className="font-semibold text-ink">{venueLabel}</span>
        </p>

        <FormField
          label={t('admin:subscription.dialog.grant_trial.days_label', {
            defaultValue: 'Trial days',
          })}
          helper={t('admin:subscription.dialog.grant_trial.days_helper', {
            defaultValue: '1–365 days. Defaults to the standard 14-day trial.',
          })}
        >
          <NumberInput
            value={trialDays}
            min={1}
            max={365}
            step={1}
            data-testid="grant-trial-days-input"
            onChange={(v) => setTrialDays(Math.round(Number(v)))}
          />
        </FormField>

        <FormField
          label={t('admin:subscription.dialog.reason_label', {
            defaultValue: 'Reason (audit-logged)',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            data-testid="grant-trial-reason"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        {serverError ? (
          <div data-testid="grant-trial-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default GrantTrialDialog;
